import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Baby, Pencil, Users, BookOpen, Heart, AlertTriangle, Filter, FileDown, FileSpreadsheet, Link2, LayoutGrid, List, CheckCircle, UserCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AddChildDialog from "@/components/dialogs/AddChildDialog";
import EditChildDialog from "@/components/dialogs/EditChildDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import { differenceInYears, differenceInMonths, parseISO, format } from "date-fns";
import { getSignedFileUrl } from "@/lib/storage-utils";
import { exportToCSV } from "@/lib/export-utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ChildProfileDialog from "@/components/children/ChildProfileDialog";
import BulkImportDialog from "@/components/children/BulkImportDialog";
import ChildCard from "@/components/children/ChildCard";
import ChildrenStatsCharts from "@/components/children/ChildrenStatsCharts";

const formatAge = (dob: string | null) => {
  if (!dob) return "-";
  const birth = parseISO(dob);
  const years = differenceInYears(new Date(), birth);
  if (years >= 1) return `${years} سنة`;
  const months = differenceInMonths(new Date(), birth);
  return `${months} شهر`;
};

const getAgeGroup = (dob: string | null): string => {
  if (!dob) return "unknown";
  const months = differenceInMonths(new Date(), parseISO(dob));
  if (months < 12) return "<1";
  if (months < 24) return "1-2";
  if (months < 36) return "2-3";
  if (months < 48) return "3-4";
  return "4+";
};

const Children = () => {
  const { profile } = useAuth();
  const [childrenList, setChildrenList] = useState<any[]>([]);
  const [classesList, setClassesList] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");
  const [parentFilter, setParentFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [editingChild, setEditingChild] = useState<any | null>(null);
  const [profileChild, setProfileChild] = useState<any | null>(null);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [parentAccounts, setParentAccounts] = useState<{ user_id: string; full_name: string | null }[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<Set<string>>(new Set());
  const [showCharts, setShowCharts] = useState(false);

  const fetchData = async () => {
    if (!profile?.nursery_id) return;
    setLoading(true);
    const today = format(new Date(), "yyyy-MM-dd");
    const [childrenRes, classesRes, parentsRes, attendanceRes] = await Promise.all([
      supabase
        .from("children")
        .select("*, classes(name)")
        .eq("nursery_id", profile.nursery_id)
        .order("created_at", { ascending: false }),
      supabase
        .from("classes")
        .select("id, name")
        .eq("nursery_id", profile.nursery_id),
      supabase
        .from("profiles")
        .select("user_id, full_name")
        .eq("nursery_id", profile.nursery_id),
      supabase
        .from("attendance")
        .select("attendee_id")
        .eq("nursery_id", profile.nursery_id)
        .eq("attendee_type", "child")
        .eq("date", today)
        .eq("status", "present"),
    ]);
    const children = childrenRes.data || [];
    setChildrenList(children);
    setClassesList(classesRes.data || []);
    setParentAccounts(parentsRes.data || []);
    setTodayAttendance(new Set((attendanceRes.data || []).map(a => a.attendee_id)));
    setLoading(false);

    const urls: Record<string, string> = {};
    for (const c of children) {
      if (c.photo_url) {
        try {
          if (c.photo_url.startsWith("http")) {
            urls[c.id] = c.photo_url;
          } else {
            const url = await getSignedFileUrl("child-photos", c.photo_url);
            if (url) urls[c.id] = url;
          }
        } catch { /* ignore */ }
      }
    }
    setPhotoUrls(urls);
  };

  useEffect(() => {
    if (profile?.nursery_id) fetchData();
  }, [profile?.nursery_id]);

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("children").delete().eq("id", id);
    if (error) {
      toast.error("خطأ في حذف الطفل: " + error.message);
    } else {
      toast.success("تم حذف بيانات الطفل بنجاح");
      fetchData();
    }
  };

  const handleQuickAttendance = async (child: any) => {
    if (!profile?.nursery_id) return;
    const today = format(new Date(), "yyyy-MM-dd");
    const { error } = await supabase.from("attendance").insert({
      nursery_id: profile.nursery_id,
      attendee_id: child.id,
      attendee_type: "child",
      date: today,
      status: "present",
      method: "manual",
      check_in_time: new Date().toISOString(),
    });
    if (error) {
      toast.error("خطأ في تسجيل الحضور: " + error.message);
    } else {
      toast.success(`تم تسجيل حضور ${child.full_name}`);
      setTodayAttendance(prev => new Set([...prev, child.id]));
    }
  };

  const filtered = useMemo(() => {
    let result = childrenList.filter((c) => {
      const matchSearch = c.full_name.toLowerCase().includes(search.toLowerCase()) ||
        (c.parent_name || "").toLowerCase().includes(search.toLowerCase());
      const matchClass = classFilter === "all" ? true :
        classFilter === "none" ? !c.class_id : c.class_id === classFilter;
      const matchAge = ageFilter === "all" ? true : getAgeGroup(c.date_of_birth) === ageFilter;
      const matchHealth = healthFilter === "all" ? true :
        healthFilter === "healthy" ? !c.health_notes : !!c.health_notes;
      const matchParent = parentFilter === "all" ? true :
        parentFilter === "linked" ? !!c.parent_id : !c.parent_id;
      return matchSearch && matchClass && matchAge && matchHealth && matchParent;
    });

    result.sort((a, b) => {
      if (sortBy === "name") return (a.full_name || "").localeCompare(b.full_name || "", "ar");
      if (sortBy === "age") {
        const ageA = a.date_of_birth ? differenceInMonths(new Date(), parseISO(a.date_of_birth)) : 0;
        const ageB = b.date_of_birth ? differenceInMonths(new Date(), parseISO(b.date_of_birth)) : 0;
        return ageB - ageA;
      }
      if (sortBy === "date") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return 0;
    });

    return result;
  }, [childrenList, search, classFilter, ageFilter, healthFilter, parentFilter, sortBy]);

  const stats = useMemo(() => {
    const total = childrenList.length;
    const withClass = childrenList.filter(c => c.class_id).length;
    const withHealth = childrenList.filter(c => c.health_notes).length;
    const linkedParents = childrenList.filter(c => c.parent_id).length;
    const byClass: Record<string, number> = {};
    childrenList.forEach(c => {
      const className = c.classes?.name || "بدون فصل";
      byClass[className] = (byClass[className] || 0) + 1;
    });
    return { total, withClass, withoutClass: total - withClass, withHealth, linkedParents, byClass };
  }, [childrenList]);

  const getExportData = useCallback(() => {
    return filtered.map((c) => ({
      "اسم الطفل": c.full_name,
      "العمر": formatAge(c.date_of_birth),
      "تاريخ الميلاد": c.date_of_birth || "-",
      "الفصل": c.classes?.name || "غير محدد",
      "ولي الأمر": c.parent_name || "-",
      "الهاتف": c.parent_phone || "-",
      "الحالة الصحية": c.health_notes ? "يوجد ملاحظات" : "سليم",
      "ملاحظات صحية": c.health_notes || "-",
    }));
  }, [filtered]);

  const exportToPDF = useCallback(async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const data = getExportData();
      const headers = ["اسم الطفل", "العمر", "الفصل", "ولي الأمر", "الهاتف", "الحالة الصحية"];

      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;width:1100px;padding:30px;background:#fff;direction:rtl;font-family:'Segoe UI',Tahoma,Arial,sans-serif;color:#1a1a1a;";
      container.innerHTML = `
        <h1 style="text-align:center;font-size:24px;margin-bottom:4px;">قائمة الأطفال</h1>
        <p style="text-align:center;font-size:12px;color:#888;margin-bottom:16px;">التاريخ: ${format(new Date(), "yyyy/MM/dd")} — عدد الأطفال: ${data.length}</p>
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#1e40af;color:#fff;">
              ${headers.map(h => `<th style="padding:8px 10px;text-align:center;">${h}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${data.map((row, idx) => `
              <tr style="background:${idx % 2 === 0 ? "#f9fafb" : "#fff"};">
                ${headers.map(h => `<td style="padding:6px 10px;text-align:center;">${row[h as keyof typeof row] || "-"}</td>`).join("")}
              </tr>
            `).join("")}
          </tbody>
        </table>
      `;
      document.body.appendChild(container);

      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");
      const pdfWidth = 277;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = 190;
      let position = 5;
      let heightLeft = pdfHeight;

      pdf.addImage(imgData, "PNG", 10, position, pdfWidth, pdfHeight);
      heightLeft -= (pageHeight - position);
      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, -(pdfHeight - heightLeft), pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`قائمة-الأطفال-${format(new Date(), "yyyy-MM-dd")}.pdf`);
      toast.success("تم تصدير القائمة كـ PDF بنجاح");
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error("خطأ في تصدير PDF");
    }
  }, [getExportData]);

  const exportToExcel = useCallback(() => {
    try {
      const data = getExportData();
      exportToCSV(data, `children-list-${format(new Date(), "yyyy-MM-dd")}.csv`);
      toast.success("تم تصدير القائمة بنجاح");
    } catch { toast.error("خطأ في تصدير الملف"); }
  }, [getExportData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">إدارة الأطفال</h1>
          <p className="text-muted-foreground text-sm mt-1">تسجيل ومتابعة بيانات الأطفال</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {filtered.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={exportToPDF}>
                <FileDown className="w-4 h-4 ml-2" />PDF
              </Button>
              <Button variant="outline" size="sm" onClick={exportToExcel}>
                <FileSpreadsheet className="w-4 h-4 ml-2" />Excel
              </Button>
            </>
          )}
          <BulkImportDialog onSuccess={fetchData} />
          <AddChildDialog onSuccess={fetchData} classes={classesList} parentAccounts={parentAccounts} />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "إجمالي الأطفال", value: stats.total, icon: Baby, color: "text-primary" },
          { label: "مسجلين بفصول", value: stats.withClass, icon: BookOpen, color: "text-primary" },
          { label: "بدون فصل", value: stats.withoutClass, icon: Users, color: "text-muted-foreground" },
          { label: "ملاحظات صحية", value: stats.withHealth, icon: Heart, color: "text-destructive" },
          { label: "مربوطين بولي أمر", value: `${stats.linkedParents}/${stats.total}`, icon: UserCheck, color: "text-primary" },
        ].map((s, i) => (
          <Card key={i} className="shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-xl font-bold text-foreground">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : s.value}
                </div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts toggle */}
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={() => setShowCharts(!showCharts)}>
          {showCharts ? "إخفاء الرسوم البيانية" : "عرض الرسوم البيانية"}
        </Button>
      </div>
      {showCharts && (
        <ChildrenStatsCharts byClass={stats.byClass} linkedParents={stats.linkedParents} total={stats.total} />
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="البحث عن طفل أو ولي أمر..." className="pr-10" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-[140px]">
            <Filter className="w-4 h-4 ml-1 text-muted-foreground" />
            <SelectValue placeholder="الفصل" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الفصول</SelectItem>
            <SelectItem value="none">بدون فصل</SelectItem>
            {classesList.map(cls => (
              <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ageFilter} onValueChange={setAgeFilter}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="العمر" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الأعمار</SelectItem>
            <SelectItem value="<1">أقل من سنة</SelectItem>
            <SelectItem value="1-2">1-2 سنة</SelectItem>
            <SelectItem value="2-3">2-3 سنة</SelectItem>
            <SelectItem value="3-4">3-4 سنة</SelectItem>
            <SelectItem value="4+">4+ سنة</SelectItem>
          </SelectContent>
        </Select>
        <Select value={healthFilter} onValueChange={setHealthFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="الصحة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="healthy">سليم</SelectItem>
            <SelectItem value="notes">ملاحظات صحية</SelectItem>
          </SelectContent>
        </Select>
        <Select value={parentFilter} onValueChange={setParentFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="ولي الأمر" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="linked">مربوط</SelectItem>
            <SelectItem value="unlinked">غير مربوط</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="ترتيب" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">الاسم</SelectItem>
            <SelectItem value="age">العمر</SelectItem>
            <SelectItem value="date">تاريخ التسجيل</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border rounded-md">
          <Button variant={viewMode === "table" ? "secondary" : "ghost"} size="icon" className="h-9 w-9 rounded-l-none" onClick={() => setViewMode("table")}>
            <List className="w-4 h-4" />
          </Button>
          <Button variant={viewMode === "grid" ? "secondary" : "ghost"} size="icon" className="h-9 w-9 rounded-r-none" onClick={() => setViewMode("grid")}>
            <LayoutGrid className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="shadow-card">
          <CardContent className="py-12 text-center">
            <Baby className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">
              {search || classFilter !== "all" || ageFilter !== "all" || healthFilter !== "all" || parentFilter !== "all"
                ? "لا توجد نتائج مطابقة" : "لم يتم تسجيل أطفال بعد. اضغط \"تسجيل طفل\" للبدء."}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((c) => (
            <ChildCard
              key={c.id}
              child={c}
              photoUrl={photoUrls[c.id]}
              formatAge={formatAge}
              onEdit={setEditingChild}
              onDelete={handleDelete}
              onViewProfile={setProfileChild}
              onQuickAttendance={handleQuickAttendance}
              todayAttendance={todayAttendance}
            />
          ))}
        </div>
      ) : (
        <Card className="shadow-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-right p-4 font-medium text-muted-foreground">الطفل</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">العمر</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">الفصل</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">ولي الأمر</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">الهاتف</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">الحالة الصحية</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">الحضور</th>
                    <th className="text-right p-4 font-medium text-muted-foreground w-24">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const isPresent = todayAttendance.has(c.id);
                    return (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80" onClick={() => setProfileChild(c)}>
                            <Avatar className="w-9 h-9">
                              <AvatarImage src={photoUrls[c.id] || ""} />
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {c.full_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-card-foreground">{c.full_name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground">{formatAge(c.date_of_birth)}</td>
                        <td className="p-4">
                          {c.classes ? (
                            <Badge variant="outline">{c.classes.name}</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground border-dashed">غير محدد</Badge>
                          )}
                        </td>
                        <td className="p-4 text-muted-foreground">
                          <div className="flex items-center gap-1">
                            {c.parent_name || "-"}
                            {c.parent_id && <Link2 className="w-3 h-3 text-primary" />}
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground" dir="ltr">{c.parent_phone || "-"}</td>
                        <td className="p-4">
                          {c.health_notes ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                              <AlertTriangle className="w-3 h-3 ml-1" />ملاحظات
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                              <Heart className="w-3 h-3 ml-1" />سليم
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <Button
                            variant={isPresent ? "secondary" : "outline"}
                            size="sm"
                            className="h-7 text-xs"
                            disabled={isPresent}
                            onClick={() => handleQuickAttendance(c)}
                          >
                            <CheckCircle className={`w-3 h-3 ml-1 ${isPresent ? "text-emerald-500" : ""}`} />
                            {isPresent ? "حاضر" : "تسجيل"}
                          </Button>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => setEditingChild(c)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <DeleteConfirmDialog
                              title="حذف الطفل"
                              description={`هل أنت متأكد من حذف "${c.full_name}"؟ لا يمكن التراجع عن هذا الإجراء.`}
                              onConfirm={() => handleDelete(c.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="text-xs text-muted-foreground text-center">
          عرض {filtered.length} من {childrenList.length} طفل
        </div>
      )}

      {editingChild && (
        <EditChildDialog
          child={editingChild}
          classes={classesList}
          parentAccounts={parentAccounts}
          open={!!editingChild}
          onOpenChange={(open) => !open && setEditingChild(null)}
          onSuccess={fetchData}
        />
      )}

      {profileChild && (
        <ChildProfileDialog
          child={profileChild}
          photoUrl={photoUrls[profileChild.id]}
          open={!!profileChild}
          onOpenChange={(open) => !open && setProfileChild(null)}
        />
      )}
    </div>
  );
};

export default Children;
